// ------------------------------------below is the code of the new endpoint for the edit student funcitonlaoity
import { axiosInstance } from './AxiosInstance';

const STUDENT_API_URL = '/studentprofiles';
const REAL_STUDENTS_API_URL = '/students';
const STUDENT_PROFILE_EXTENDED_URL = '/studentprofiles';
const ATTENDANCE_API_URL = '/attendances';
const STUDENTS_PAGE_CAP = 100;
const STUDENTS_DEFAULT_LIMIT = 10;

// Student CRUD Operations
export const getStudents = async (limit = 10, page = 1, search = '', includeTotal = true) => {
  try {
    const safeLimit = Math.max(1, Math.min(STUDENTS_PAGE_CAP, Number.parseInt(limit, 10) || STUDENTS_DEFAULT_LIMIT));
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const safeSearch = search || '';
    const response = await axiosInstance.get(
      `${STUDENT_API_URL}?limit=${safeLimit}&page=${safePage}&search=${safeSearch}&includeTotal=${includeTotal ? 'true' : 'false'}`
    );
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error('Error fetching students', error);
    throw error;
  }
};

// NEW: Get multiple students by their IDs
export const getStudentsByIds = async (ids = []) => {
  try {
    if (!ids || ids.length === 0) return { data: [] };
    const idsString = Array.isArray(ids) ? ids.join(',') : ids;
    
    // Pass rawUserIds so the backend knows to filter by an exact array of IDs
    const response = await axiosInstance.get(
      `${REAL_STUDENTS_API_URL}?limit=${ids.length + 50}&rawUserIds=${idsString}`
    );
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error('Error fetching students by IDs', error);
    throw error;
  }
};

// NEW: Get all students from /students endpoint
export const getAllStudentsFromStudentsAPI = async (limit = 10, page = 1, search = '', includeTotal = true) => {
  try {
    const safeLimit = Math.max(1, Math.min(STUDENTS_PAGE_CAP, Number.parseInt(limit, 10) || STUDENTS_DEFAULT_LIMIT));
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const safeSearch = search || '';
    const response = await axiosInstance.get(
      `${REAL_STUDENTS_API_URL}?limit=${safeLimit}&page=${safePage}&search=${safeSearch}&includeTotal=${includeTotal ? 'true' : 'false'}`
    );
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error('Error fetching students from students API', error);
    throw error;
  }
};

/**
 * One page of students for a picker: { id, name, email } per row. Same
 * name-or-email search and paging as getAllStudentsFromStudentsAPI, without
 * the rest of each record or its joined course / contact / branch data.
 */
export const getStudentOptions = async (limit = 10, page = 1, search = '', includeTotal = true) => {
  const params = {
    limit: Math.max(1, Math.min(STUDENTS_PAGE_CAP, Number.parseInt(limit, 10) || STUDENTS_DEFAULT_LIMIT)),
    page: Math.max(1, Number.parseInt(page, 10) || 1),
    fields: 'options',
  };
  // A picker that pages by "was this page full?" needs no count query.
  if (!includeTotal) params.includeTotal = 'false';
  // Passed as a param, so axios encodes it -- a search with & or # in it
  // no longer breaks the query string.
  if (search && String(search).trim()) params.search = String(search).trim();
  const response = await axiosInstance.get(REAL_STUDENTS_API_URL, { params });
  return { data: response.data.data, pagination: response.data.pagination };
};

// Get student by ID from /students endpoint
export const getStudentByIdFromStudents = async (studentId) => {
  try {
    const response = await axiosInstance.get(`${REAL_STUDENTS_API_URL}/${studentId}`);
    return {
      data: response.data.data,
      status: response.status,
      message: response.data.message,
    };
  } catch (error) {
    console.error('Error fetching student by ID from students API', error);
    throw error;
  }
};

export const createStudent = async (studentData) => {
  try {
    const response = await axiosInstance.post(STUDENT_API_URL, studentData);
    return response.data;
  } catch (error) {
    console.error('Error creating student', {
      error: error.response?.data,
      requestData: studentData,
    });
    throw error;
  }
};

export const updateStudent = async (studentId, studentData) => {
  try {
    const response = await axiosInstance.put(`${STUDENT_API_URL}/${studentId}`, studentData);
    return response.data;
  } catch (error) {
    console.error('Error updating student', error);
    throw error;
  }
};

// Update student in /students endpoint
export const updateStudentInStudentsAPI = async (studentId, studentData) => {
  try {
    const response = await axiosInstance.put(`${REAL_STUDENTS_API_URL}/${studentId}`, studentData);
    return response.data;
  } catch (error) {
    console.error('Error updating student in students API', error);
    throw error;
  }
};

export const deleteStudent = async (studentId) => {
  try {
    const response = await axiosInstance.delete(`${STUDENT_API_URL}/${studentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting student', error);
    throw error;
  }
};

export const getStudentById = async (studentId) => {
  try {
    const response = await axiosInstance.get(`${STUDENT_API_URL}/${studentId}`);
    return {
      data: response.data.data,
      status: response.status,
      message: response.data.message,
    };
  } catch (error) {
    console.error('Error fetching student details', error);
    throw error;
  }
};

// Student Profile Operations
export const getStudentProfile = async () => {
  try {
    const response = await axiosInstance.get(`${STUDENT_API_URL}/profile`);
    return response.data;
  } catch (error) {
    console.error('Error fetching unified student profile', error);
    throw error;
  }
};

export const getStudentProfileById = async (studentId) => {
  try {
    const response = await axiosInstance.get(`${STUDENT_API_URL}/${studentId}`);
    return {
      data: response.data.data,
      status: response.status,
      message: response.data.message,
    };
  } catch (error) {
    console.error('Error fetching student profile', error);
    throw error;
  }
};

export const updateStudentProfile = async (studentId, profileData) => {
  try {
    const response = await axiosInstance.put(`${STUDENT_API_URL}/${studentId}`, profileData);
    return response.data;
  } catch (error) {
    console.error('Error updating student profile', error);
    throw error;
  }
};

// NEW: Update both receipts and students endpoints
export const updateStudentProfileComplete = async (receiptId, studentId, profileData) => {
  try {
    const results = {
      receipts: null,
      students: null,
      errors: [],
    };

    // Update /receipts endpoint if receiptId is provided
    if (receiptId) {
      try {
        const receiptsResponse = await axiosInstance.put(
          `${STUDENT_API_URL}/${receiptId}`,
          profileData
        );
        results.receipts = receiptsResponse.data;
      } catch (error) {
        console.error('Error updating receipts endpoint', error);
        results.errors.push({ endpoint: 'receipts', error });
      }
    }

    // Update /students endpoint if studentId is provided
    if (studentId) {
      try {
        const studentsResponse = await axiosInstance.put(
          `${STUDENT_API_URL}/${studentId}`,
          profileData
        );
        results.students = studentsResponse.data;
      } catch (error) {
        console.error('Error updating students endpoint', error);
        results.errors.push({ endpoint: 'students', error });
      }
    }

    // Return results - at least one should succeed
    if (results.receipts || results.students) {
      return {
        success: true,
        data: results,
        message: 'Profile updated successfully',
      };
    } else {
      throw new Error('Failed to update both endpoints');
    }
  } catch (error) {
    console.error('Error updating student profile completely', error);
    throw error;
  }
};

// ============= NEW: EXTENDED PROFILE API FUNCTIONS =============

export const createOrUpdateExtendedProfile = async (studentId, extendedData) => {
  try {
    // **STEP 1: Check if profile already exists for this userId**
    const existingProfileResponse = await findExtendedProfileByUserId(studentId);
    const existingProfile = existingProfileResponse.data;

    // **STEP 2: If profile exists, UPDATE it instead of creating new**
    if (existingProfile && existingProfile.id) {
      // console.log("Existing profile found, updating profile ID:", existingProfile.id)

      const response = await axiosInstance.put(
        `${STUDENT_PROFILE_EXTENDED_URL}/${existingProfile.id}`,
        {
          userId: studentId,
          ...extendedData,
        }
      );

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Extended profile updated successfully',
      };
    }
    // **STEP 3: If no profile exists, CREATE new one**
    else {
      // console.log("No existing profile found, creating new profile for userId:", studentId)

      const response = await axiosInstance.post(`${STUDENT_PROFILE_EXTENDED_URL}`, {
        userId: studentId,
        ...extendedData,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Extended profile created successfully',
      };
    }
  } catch (error) {
    console.error('Error creating/updating extended profile', error);
    throw error;
  }
};

/**
 * Update existing extended profile by its ID
 */
export const updateExtendedProfileById = async (profileId, extendedData) => {
  try {
    const response = await axiosInstance.put(`${STUDENT_PROFILE_EXTENDED_URL}/${profileId}`, {
      ...extendedData,
    });
    return {
      success: true,
      data: response.data.data,
      message: response.data.message || 'Extended profile updated successfully',
    };
  } catch (error) {
    console.error('Error updating extended profile by ID', error);
    throw error;
  }
};

/**
 * Get extended profile data by studentId
 */
export const getExtendedProfileByStudentId = async (studentId) => {
  try {
    const response = await axiosInstance.get(`${STUDENT_PROFILE_EXTENDED_URL}/${studentId}`);
    return {
      data: response.data.data,
      status: response.status,
      message: response.data.message,
    };
  } catch (error) {
    // If no extended profile found, return null instead of throwing error
    if (error.response && error.response.status === 404) {
      return {
        data: null,
        status: 404,
        message: 'No extended profile found',
      };
    }
    console.error('Error fetching extended profile', error);
    throw error;
  }
};

export const updateCompleteStudentProfile = async (
  receiptId,
  studentId,
  basicData,
  extendedData
) => {
  try {
    const results = {
      receipts: null,
      students: null,
      extendedProfile: null,
      errors: [],
    };

    // Update /receipts endpoint
    if (receiptId && basicData) {
      try {
        const receiptsResponse = await axiosInstance.put(
          `${STUDENT_API_URL}/${receiptId}`,
          basicData
        );
        results.receipts = receiptsResponse.data;
      } catch (error) {
        console.error('Error updating receipts endpoint', error);
        results.errors.push({ endpoint: 'receipts', error });
      }
    }

    // Update /students endpoint
    if (studentId && basicData) {
      try {
        const studentsResponse = await axiosInstance.put(
          `${STUDENT_API_URL}/${studentId}`,
          basicData
        );
        results.students = studentsResponse.data;
      } catch (error) {
        console.error('Error updating students endpoint', error);
        results.errors.push({ endpoint: 'students', error });
      }
    }

    // **NEW: Update extended profile endpoint - checks if exists first**
    if (studentId && extendedData) {
      try {
        const extendedResponse = await createOrUpdateExtendedProfile(studentId, extendedData);
        results.extendedProfile = extendedResponse.data;
      } catch (error) {
        console.error('Error updating extended profile endpoint', error);
        results.errors.push({ endpoint: 'extendedProfile', error });
      }
    }

    // Return results - at least one should succeed
    if (results.receipts || results.students || results.extendedProfile) {
      return {
        success: true,
        data: results,
        message: 'Complete profile updated successfully',
      };
    } else {
      throw new Error('Failed to update any endpoints');
    }
  } catch (error) {
    console.error('Error updating complete student profile', error);
    throw error;
  }
};

// Fill missing profile data from /students API and extended profile
// Fill missing profile data from /students API and extended profile
export const fillMissingProfileData = async (receiptData) => {
  try {
    // Check if essential fields are missing
    const missingFields =
      !receiptData.phone || !receiptData.documentNo || !receiptData.city || !receiptData.state;

    let mergedData = { ...receiptData };

    // Get data from /students API if missing basic fields
    if (missingFields && receiptData.studentId) {
      try {
        const studentResponse = await getStudentByIdFromStudents(receiptData.studentId);
        const studentData = studentResponse.data;

        if (studentData) {
          mergedData = {
            ...mergedData,
            phone: mergedData.phone || studentData.phone || '',
            gender: mergedData.gender || studentData.gender || '',
            city: mergedData.city || studentData.city || '',
            state: mergedData.state || studentData.state || '',
            documentNo: mergedData.documentNo || studentData.documentNo || '',
            documentType: mergedData.documentType || studentData.documentType || '',
            collegeName: mergedData.collegeName || studentData.collegeName || '',
            profession: mergedData.profession || studentData.profession || '',
            _dataSource: 'merged-basic',
          };
        }
      } catch (error) {
        console.error('Error fetching additional student data:', error);
      }
    }

    // **NEW: Find extended profile by matching userId with studentId (NOT by URL ID)**
    if (receiptData.studentId) {
      try {
        const extendedProfileResponse = await findExtendedProfileByUserId(receiptData.studentId);
        const extendedData = extendedProfileResponse.data;

        if (extendedData) {
          // Merge extended profile fields into profile data
          mergedData = {
            ...mergedData,
            permanentAddress: extendedData.permanentAddress || '',
            currentAddress: extendedData.currentAddress || '',
            tenthMarks: extendedData.tenthMarks || '',
            twelfthMarks: extendedData.twelfthMarks || '',
            _dataSource: 'merged-complete',
            _extendedProfileId: extendedData.id || extendedData._id, // Store ID for future updates
          };
        }
      } catch (error) {
        console.error('Error fetching extended profile data:', error);
        // If no extended profile exists, these fields will remain empty
      }
    }

    return mergedData;
  } catch (error) {
    console.error('Error filling missing profile data:', error);
    return receiptData;
  }
};

// Attendance Operations
export const getStudentAttendancePercentage = async (studentId, batchId) => {
  try {
    const response = await axiosInstance.get(`${ATTENDANCE_API_URL}/summary`, {
      params: { studentId, batchId },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching attendance percentage', error);
    throw error;
  }
};

// Combined operations that might need both student and profile data
export const getCompleteStudentDetails = async (studentId) => {
  try {
    const [studentResponse, profileResponse] = await Promise.all([
      axiosInstance.get(`${STUDENT_API_URL}/${studentId}`),
      axiosInstance.get(`${STUDENT_API_URL}/${studentId}`),
    ]);

    return {
      student: studentResponse.data.data,
      profile: profileResponse.data.data,
    };
  } catch (error) {
    console.error('Error fetching complete student details', error);
    throw error;
  }
};

/**
 * Get ALL extended profiles and find by matching userId with studentId
 */
export const getAllExtendedProfiles = async (limit = 10, page = 1, search = '', includeTotal = true) => {
  try {
    const safeLimit = Math.max(1, Math.min(STUDENTS_PAGE_CAP, Number.parseInt(limit, 10) || STUDENTS_DEFAULT_LIMIT));
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const safeSearch = search || '';
    const response = await axiosInstance.get(
      `${STUDENT_PROFILE_EXTENDED_URL}?limit=${safeLimit}&page=${safePage}&search=${safeSearch}&includeTotal=${includeTotal ? 'true' : 'false'}`
    );
    return {
      data: response.data.data || [],
      status: response.status,
      message: response.data.message,
    };
  } catch (error) {
    console.error('Error fetching all extended profiles', error);
    throw error;
  }
};

/**
 * Find extended profile by matching userId with studentId from receipt
 */
export const findExtendedProfileByUserId = async (studentId) => {
  try {
    // Get all profiles
    const response = await getAllExtendedProfiles();
    const allProfiles = response.data;

    // Find profile where userId matches the studentId from receipt
    const matchedProfile = allProfiles.find((profile) => profile.userId === studentId);

    if (matchedProfile) {
      return {
        data: matchedProfile,
        status: 200,
        message: 'Extended profile found',
      };
    } else {
      return {
        data: null,
        status: 404,
        message: 'No extended profile found',
      };
    }
  } catch (error) {
    console.error('Error finding extended profile by userId', error);
    return {
      data: null,
      status: 404,
      message: 'No extended profile found',
    };
  }
};
